import { useState } from 'react';
import { Link, NavLink, Route, Routes, useParams } from 'react-router';
import { api, openAuthenticatedFile } from '../../api/client.js';
import type { SessionUser } from '../../api/types.js';
import {
  Alert,
  Field,
  Notice,
  PageHeader,
  StatusPill,
  datum,
  eur,
  fehlertext,
  useLoad,
} from '../shared/ui.js';
import { KONTOTYP } from './AccountsPage.js';
import { ConditionsTab } from './ConditionsTab.js';

interface Account {
  id: string;
  accountNumber: string;
  name: string;
  accountType: string;
  isActive: boolean;
  addressJson: { street?: string; postalCode?: string; city?: string; country?: string };
  salesOwner: { id: string; displayName: string; email: string } | null;
  createdAt: string;
  _count: { users: number; quotes: number; conditionVersions: number };
}

/** Kundendetail mit den Bereichen Uebersicht, Konditionen, Benutzer, Angebote, Historie. */
export function AccountDetailPage({ user }: { user: SessionUser }): React.JSX.Element {
  const { id } = useParams();
  const {
    data: konto,
    error,
    reload,
  } = useLoad<Account>(id ? `/api/admin/accounts/${id}` : null);

  if (error !== null) return <Alert text={error} />;
  if (konto === null || id === undefined) return <p className="muted">Wird geladen …</p>;

  /**
   * Absolute Ziele aus einer einzigen stabilen Basis, nie relativ.
   *
   * `AccountDetailPage` haengt an einer Splat-Route (`kunden/:id/*`,
   * `AdminShell.tsx`) und rendert selbst verschachtelte `<Routes>` fuer die
   * einzelnen Bereiche. Ein relatives `NavLink to="konditionen"` loest React
   * Router dabei NICHT gegen dieses feste Praefix auf, sondern gegen den
   * gesamten aktuell passenden Splat-Anteil der URL (den bereits aktiven
   * Unterpfad) -- ein Wechsel von "Benutzer" zu "Konditionen" haengte deshalb
   * `konditionen` an `.../benutzer` an, statt es zu ersetzen, und die URL wuchs
   * bei jedem Tabwechsel weiter. Absolute Pfade sind von dieser
   * Splat-Aufloesung nicht betroffen.
   */
  const basisRoute = `/admin/kunden/${id}`;
  const tab = (pfad: string, text: string) => (
    <NavLink
      to={pfad === '' ? basisRoute : `${basisRoute}/${pfad}`}
      end={pfad === ''}
      className={({ isActive }) => (isActive ? 'tab selected' : 'tab')}
    >
      {text}
    </NavLink>
  );

  return (
    <>
      <PageHeader
        title={konto.name}
        lead={`Kundennummer ${konto.accountNumber} · ${KONTOTYP[konto.accountType] ?? konto.accountType}`}
        actions={<StatusPill active={konto.isActive} />}
      />
      <nav className="tabs" aria-label="Kundenbereiche">
        {tab('', 'Uebersicht')}
        {tab('konditionen', `Konditionen (${konto._count.conditionVersions})`)}
        {tab('benutzer', `Benutzer (${konto._count.users})`)}
        {tab('angebote', `Angebote (${konto._count.quotes})`)}
        {tab('historie', 'Historie')}
      </nav>
      <Routes>
        <Route index element={<Uebersicht konto={konto} reload={reload} />} />
        <Route
          path="konditionen"
          element={<ConditionsTab accountId={id} user={user} />}
        />
        <Route path="benutzer" element={<BenutzerTab accountId={id} />} />
        <Route path="angebote" element={<AngeboteTab accountId={id} />} />
        <Route path="historie" element={<HistorieTab accountId={id} />} />
      </Routes>
    </>
  );
}

function Uebersicht({
  konto,
  reload,
}: {
  konto: Account;
  reload: () => void;
}): React.JSX.Element {
  const intern = useLoad<{ items: Array<{ id: string; displayName: string }> }>(
    '/api/admin/accounts/internal-users',
  );
  const [bearbeiten, setBearbeiten] = useState(false);
  const [f, setF] = useState({
    name: konto.name,
    accountType: konto.accountType,
    street: konto.addressJson.street ?? '',
    postalCode: konto.addressJson.postalCode ?? '',
    city: konto.addressJson.city ?? '',
    salesOwnerId: konto.salesOwner?.id ?? '',
  });
  const [error, setError] = useState<string | null>(null);

  async function speichern(): Promise<void> {
    setError(null);
    try {
      await api.patch(`/api/admin/accounts/${konto.id}`, {
        name: f.name,
        accountType: f.accountType,
        address: { street: f.street, postalCode: f.postalCode, city: f.city },
        salesOwnerId: f.salesOwnerId === '' ? null : f.salesOwnerId,
      });
      setBearbeiten(false);
      reload();
    } catch (e) {
      setError(fehlertext(e));
    }
  }

  async function statusWechsel(): Promise<void> {
    const frage = konto.isActive
      ? `Kunde ${konto.name} deaktivieren? Bestehende Angebote und Zugaenge bleiben erhalten; der Kunde kann sich nicht mehr anmelden.`
      : `Kunde ${konto.name} wieder aktivieren?`;
    if (!window.confirm(frage)) return;
    try {
      await api.patch(`/api/admin/accounts/${konto.id}`, { isActive: !konto.isActive });
      reload();
    } catch (e) {
      setError(fehlertext(e));
    }
  }

  const a = konto.addressJson;
  return (
    <section className="card">
      {!bearbeiten ? (
        <>
          <dl className="kopfdaten wide">
            <dt>Kundenname</dt>
            <dd>{konto.name}</dd>
            <dt>Kundennummer</dt>
            <dd>
              <code>{konto.accountNumber}</code>
            </dd>
            <dt>Kundentyp</dt>
            <dd>{KONTOTYP[konto.accountType] ?? konto.accountType}</dd>
            <dt>Anschrift</dt>
            <dd>
              {[a.street, [a.postalCode, a.city].filter(Boolean).join(' '), a.country]
                .filter(Boolean)
                .join(', ') || <span className="muted">keine</span>}
            </dd>
            <dt>Zustaendiger Vertrieb</dt>
            <dd>
              {konto.salesOwner ? (
                `${konto.salesOwner.displayName} (${konto.salesOwner.email})`
              ) : (
                <span className="muted">nicht zugeordnet</span>
              )}
            </dd>
            <dt>Angelegt</dt>
            <dd>{datum(konto.createdAt)}</dd>
          </dl>
          <div className="row">
            <button type="button" className="primary" onClick={() => setBearbeiten(true)}>
              Stammdaten bearbeiten
            </button>
            <button type="button" onClick={() => void statusWechsel()}>
              {konto.isActive ? 'Deaktivieren' : 'Aktivieren'}
            </button>
          </div>
          <p className="note">
            Kunden werden nicht geloescht: Angebote und Historie bleiben nachvollziehbar.
            Ein deaktivierter Kunde kann sich nicht anmelden.
          </p>
        </>
      ) : (
        <>
          <div className="grid2">
            <Field label="Kundenname">
              <input
                value={f.name}
                onChange={(e) => setF({ ...f, name: e.target.value })}
              />
            </Field>
            <Field label="Kundentyp">
              <select
                value={f.accountType}
                onChange={(e) => setF({ ...f, accountType: e.target.value })}
              >
                <option value="distributor">Haendler</option>
                <option value="field_sales">Aussendienst</option>
                <option value="end_customer">Endkunde</option>
              </select>
            </Field>
            <Field label="Strasse">
              <input
                value={f.street}
                onChange={(e) => setF({ ...f, street: e.target.value })}
              />
            </Field>
            <Field label="PLZ">
              <input
                value={f.postalCode}
                onChange={(e) => setF({ ...f, postalCode: e.target.value })}
              />
            </Field>
            <Field label="Ort">
              <input
                value={f.city}
                onChange={(e) => setF({ ...f, city: e.target.value })}
              />
            </Field>
            <Field label="Zustaendiger Vertrieb">
              <select
                value={f.salesOwnerId}
                onChange={(e) => setF({ ...f, salesOwnerId: e.target.value })}
              >
                <option value="">– niemand –</option>
                {(intern.data?.items ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Alert text={error} />
          <div className="row">
            <button type="button" className="primary" onClick={() => void speichern()}>
              Speichern
            </button>
            <button type="button" onClick={() => setBearbeiten(false)}>
              Abbrechen
            </button>
          </div>
        </>
      )}
      <Alert text={bearbeiten ? null : error} />
    </section>
  );
}

function BenutzerTab({ accountId }: { accountId: string }): React.JSX.Element {
  const { data, error, reload } = useLoad<{
    items: Array<{
      id: string;
      email: string;
      displayName: string;
      role: string;
      isActive: boolean;
      lastLoginAt: string | null;
      mustChangePassword: boolean;
    }>;
  }>(`/api/admin/accounts/${accountId}/users`);
  const [neu, setNeu] = useState({ email: '', displayName: '', role: 'account_user' });
  const [geheim, setGeheim] = useState<{ email: string; pw: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function anlegen(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    try {
      const r = await api.post<{ initialPassword: string }>(
        `/api/admin/accounts/${accountId}/users`,
        neu,
      );
      setGeheim({ email: neu.email, pw: r.initialPassword });
      setNeu({ email: '', displayName: '', role: 'account_user' });
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function reset(userId: string, email: string): Promise<void> {
    if (
      !window.confirm(
        `Passwort fuer ${email} zuruecksetzen? Das bisherige wird ungueltig.`,
      )
    )
      return;
    try {
      const r = await api.post<{ initialPassword: string }>(
        `/api/admin/users/${userId}/reset-password`,
      );
      setGeheim({ email, pw: r.initialPassword });
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  async function status(userId: string, aktiv: boolean): Promise<void> {
    try {
      await api.patch(`/api/admin/users/${userId}`, { isActive: aktiv });
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <>
      {geheim !== null && (
        <Notice>
          <strong>Einmalpasswort fuer {geheim.email}:</strong>{' '}
          <code className="secret">{geheim.pw}</code>
          <p className="note">
            Wird nur jetzt angezeigt und nirgends gespeichert. Der Benutzer muss es bei
            der naechsten Anmeldung aendern.
          </p>
          <button type="button" className="link" onClick={() => setGeheim(null)}>
            Ausblenden
          </button>
        </Notice>
      )}
      <section className="card">
        <h3>Zugaenge</h3>
        <Alert text={error ?? err} />
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Rolle</th>
                <th>Status</th>
                <th>Letzte Anmeldung</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).map((u) => (
                <tr key={u.id}>
                  <td>{u.displayName}</td>
                  <td>{u.email}</td>
                  <td>
                    {u.role === 'account_admin' ? 'Kontoadmin' : 'Benutzer'}
                    {u.mustChangePassword && (
                      <span className="note"> · Passwortwechsel offen</span>
                    )}
                  </td>
                  <td>
                    <StatusPill active={u.isActive} />
                  </td>
                  <td>
                    {u.lastLoginAt ? (
                      datum(u.lastLoginAt)
                    ) : (
                      <span className="muted">noch nie</span>
                    )}
                  </td>
                  <td className="row">
                    <button
                      type="button"
                      className="link"
                      onClick={() => void reset(u.id, u.email)}
                    >
                      Passwort zuruecksetzen
                    </button>
                    <button
                      type="button"
                      className="link"
                      onClick={() => void status(u.id, !u.isActive)}
                    >
                      {u.isActive ? 'deaktivieren' : 'aktivieren'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <section className="card">
        <h3>Zugang anlegen</h3>
        <form onSubmit={anlegen} className="grid2">
          <Field label="E-Mail-Adresse">
            <input
              type="email"
              required
              value={neu.email}
              onChange={(e) => setNeu({ ...neu, email: e.target.value })}
            />
          </Field>
          <Field label="Anzeigename">
            <input
              required
              value={neu.displayName}
              onChange={(e) => setNeu({ ...neu, displayName: e.target.value })}
            />
          </Field>
          <Field label="Rolle">
            <select
              value={neu.role}
              onChange={(e) => setNeu({ ...neu, role: e.target.value })}
            >
              <option value="account_user">Benutzer</option>
              <option value="account_admin">Kontoadmin</option>
            </select>
          </Field>
          <div className="row" style={{ alignSelf: 'end' }}>
            <button type="submit" className="primary">
              Anlegen
            </button>
          </div>
        </form>
      </section>
    </>
  );
}

function AngeboteTab({ accountId }: { accountId: string }): React.JSX.Element {
  const { data, error } = useLoad<{
    items: Array<{
      quoteId: string;
      quoteNumber: string | null;
      status: string;
      quoteDate: string | null;
      documentTotalNet: string | null;
      itemCount: number;
      pdfAvailable: boolean;
    }>;
  }>(`/api/admin/accounts/${accountId}/quotes`);
  return (
    <section className="card">
      <Alert text={error} />
      {data !== null && data.items.length === 0 && (
        <p className="muted">Noch keine Angebote.</p>
      )}
      {data !== null && data.items.length > 0 && (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Angebotsnummer</th>
                <th>Datum</th>
                <th>Status</th>
                <th className="num">Positionen</th>
                <th className="num">Gesamt netto</th>
                <th>PDF</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((q) => (
                <tr key={q.quoteId}>
                  <td>
                    <code>{q.quoteNumber ?? '–'}</code>
                  </td>
                  <td>{datum(q.quoteDate)}</td>
                  <td>
                    {q.status === 'issued'
                      ? 'ausgestellt'
                      : q.status === 'draft'
                        ? 'Entwurf'
                        : q.status}
                  </td>
                  <td className="num">{q.itemCount}</td>
                  <td className="num">{eur(q.documentTotalNet)}</td>
                  <td>
                    <div className="row">
                      <Link to={`/admin/angebote/${q.quoteId}`}>Details</Link>
                      {q.pdfAvailable ? (
                        <button
                          type="button"
                          className="link"
                          onClick={() => {
                            void openAuthenticatedFile(
                              `/api/quotes/${q.quoteId}/pdf`,
                            ).catch(() => undefined);
                          }}
                        >
                          PDF
                        </button>
                      ) : (
                        <span className="muted">–</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function HistorieTab({ accountId }: { accountId: string }): React.JSX.Element {
  const { data, error } = useLoad<{
    items: Array<{
      id: string;
      occurredAt: string;
      entity: string;
      action: string;
      actor: { displayName: string };
      afterJson: unknown;
    }>;
  }>(`/api/admin/accounts/${accountId}/audit`);
  const ENTITY: Record<string, string> = {
    account: 'Kunde',
    app_user: 'Benutzer',
    account_condition_version: 'Kondition',
    quote: 'Angebot',
  };
  const ACTION: Record<string, string> = {
    create: 'angelegt',
    update: 'geaendert',
    delete: 'geloescht',
    activate: 'aktiviert',
    deactivate: 'deaktiviert',
  };
  return (
    <section className="card">
      <p className="note">
        Wirtschaftlich relevante Aenderungen an diesem Kunden, neueste zuerst.
      </p>
      <Alert text={error} />
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Zeitpunkt</th>
              <th>Was</th>
              <th>Aktion</th>
              <th>Durch</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((e) => (
              <tr key={e.id}>
                <td>{new Date(e.occurredAt).toLocaleString('de-DE')}</td>
                <td>{ENTITY[e.entity] ?? e.entity}</td>
                <td>{ACTION[e.action] ?? e.action}</td>
                <td>{e.actor.displayName}</td>
                <td>
                  <code className="small">
                    {e.afterJson ? JSON.stringify(e.afterJson) : ''}
                  </code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
